import { Constants, Epsilon, Scene, UniversalCamera, Vector3, serialize } from '@babylonjs/core';
import { ICamera } from 'Framework/Components/Controls/XView/Cameras/ICamera';
import { ICoordinates } from 'Framework/Components/Controls/XView/Core';
import { ZoomAndPanMouseInput, ZoomAndPanPointersInput } from 'Framework/Components/Controls/XView/Inputs';
import { MathHelper } from 'Framework/Helpers/MathHelper';

export class Flat2DCamera extends UniversalCamera implements ICamera {
    public constructor(name: string, scene: Scene) {
        super(name, new Vector3(0, 0, -0.5), scene);

        this.fovMode = Constants.FOVMODE_HORIZONTAL_FIXED;
        this.minZ = 0.1;
        this.inputs.clear();
        this.inputs.add(new ZoomAndPanPointersInput());
        this.inputs.add(new ZoomAndPanMouseInput());
    }

    public lookTo(position: Vector3): void;
    public lookTo(position: ICoordinates): void;
    public lookTo(position: unknown): void {
        if (position instanceof Vector3
            || MathHelper.isPlanCoordinates(position)
        ) {
            this.position = new Vector3(position.x, position.y, -0.5);
        }
        else {
            throw new Error('Method lookTo with unknown position not implemented.');
        }
    }

    /**
     * Defines pitch rotation of the camera along the X axis.
     * @param pitch pitch in radian
     */
    public setPitch(pitch: number): void {
        this.rotation.x = pitch;
    }

    /**
     * Defines yaw rotation of the camera along the Y axis.
     * @param yaw yaw in radian
     */
    public setYaw(yaw: number): void {
        this.rotation.y = yaw;
    }

    /**
     * Defines roll rotation of the camera along the Z axis.
     * @param roll roll in radian
     */
    public setRoll(roll: number): void {
        this.rotation.z = roll;
    }

    /** @internal */
    public _checkInputs(): void {
        if (this.inertialRadiusOffset !== 0) {
            this.fov -= this.inertialRadiusOffset;
            this.inertialRadiusOffset *= this.inertia;

            if (Math.abs(this.inertialRadiusOffset) < this.speed * Epsilon) {
                this.inertialRadiusOffset = 0;
            }
        }

        // Limits
        this._checkLimits();

        super._checkInputs();
    }

    protected _checkLimits() {
        if (this.minFov !== null && this.fov < this.minFov) {
            this.fov = this.minFov;
            this.inertialRadiusOffset = 0;
        }

        if (this.maxFov !== null && this.fov > this.maxFov) {
            this.fov = this.maxFov;
            this.inertialRadiusOffset = 0;
        }
    }

    public maxFov: number;
    public minFov: number;

    /**
     * Current inertia value on the radius axis.
     * The bigger this number the longer it will take for the camera to stop.
     */
    @serialize()
    public inertialRadiusOffset: number = 0;
}
